import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  DOCUMENT,
  inject
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  CoreActionNavigateToResource,
  CoreMobiResourceType,
  CorePageModuleIDs,
  CoreRouterActionNavigateToPageModule,
  CoreUtilsService
} from '@bepatient/core';
import { Store } from '@ngrx/store';
import { CommonUtilsService } from './../services/common-utils.service';

@Component({
  selector: 'bepatient-html',
  templateUrl: './bepatient-html.component.html',
  styleUrls: ['./bepatient-html.component.scss'],
  standalone: false
})
export class BepatientHtmlComponent implements OnInit, AfterViewInit {
  private store = inject<Store<any>>(Store);
  private utils = inject(CommonUtilsService);
  private coreUtils = inject(CoreUtilsService);
  private domSanitizer = inject(DomSanitizer);
  private document = inject<HTMLDocument>(DOCUMENT);

  @Input() title = '';
  @Input() color = '';
  @Input() content: string;
  @Input() forTosDetails = false;

  domElements: Array<any> = new Array<any>();

  spinner: HTMLElement;
  safeHtml: SafeHtml;

  ngOnInit() {
    if (this.content) {
      const parsedContent = this.content.replace(/<img/g, `<img id='img'`);
      this.safeHtml = this.domSanitizer.bypassSecurityTrustHtml(parsedContent);
    }

    // If has <a> tags - extract them as an array of <a> tags
    if (this.content && this.content.match(/<a(.)*>.*?<\/a>/g)) {
      const aArr = this.content.match(/<a(.)*?>.*?<\/a>/g);
      const root = this.document.createElement('div'); // for getting inner text inside <a> tags later
      let htmlString = this.content;

      // Parsing html as many times as many <a> tags are in the html.
      // If there is anything before an <a> tag - saving it as a html type, sanitize it, and push into an array
      for (let i = 0; i < aArr.length; i++) {
        if (htmlString.indexOf(aArr[i]) > 0) {
          const elem = htmlString.substr(0, htmlString.indexOf(aArr[i]));
          const sanitizedElem = this.domSanitizer.bypassSecurityTrustHtml(elem);
          const htmlElem = { type: 'html', el: sanitizedElem };
          this.domElements.push(htmlElem);
          htmlString = htmlString.substring(elem.length); // delete the used part out of the initial html string
        }
        // If there is nothing before an <a> tag - saving it as a button type,
        // assign inner text ot it, and push it into an array
        root.innerHTML = aArr[i];
        const buttonElem = { type: 'button', el: '' };
        [].map.call(root.querySelectorAll('a'), function (v) {
          buttonElem.el = v.innerText;
        });
        this.domElements.push(buttonElem);
        htmlString = htmlString.substring(aArr[i].length); // delete the used part out of the initial html string
      }
      // When all the element are cropped out of the html, check for the closing bit
      if (htmlString) {
        const sanitizedElem =
          this.domSanitizer.bypassSecurityTrustHtml(htmlString);
        const htmlElem = { type: 'html', el: sanitizedElem };
        this.domElements.push(htmlElem);
      }
    } else if (this.content) {
      const sanitizedElem = this.domSanitizer.bypassSecurityTrustHtml(
        this.content
      );
      const htmlElem = { type: 'html', el: sanitizedElem };
      this.domElements.push(htmlElem);
    }
  }

  ngAfterViewInit(): void {
    // Adding spinner
    this.spinner = document.createElement('ion-spinner');
    this.spinner.setAttribute('name', 'dots');
    this.spinner.style.alignSelf = 'center';
    this.spinner.style.top = '16px';

    const img = document.getElementById('img');
    const frame = document.getElementById('html-content');
    frame.prepend(this.spinner);

    if (!img) {
      this.spinner.remove();
    } else {
      img.addEventListener('load', () => {
        this.spinner.remove();
      });
      img.addEventListener('error', () => {
        this.spinner.remove();
      });
    }
  }

  htmlItemClick(urlName: string): void {
    // Create an array for chosen <a> tag
    let chosenAtag: Array<string> = new Array<string>();

    // Extract the all <a> tags out of href into an array
    const aArr = this.content.match(/<a(.)*?>.*?<\/a>/g);

    // Href can contain several <a> tags, we loop through to find chosen by <a> tag name
    if (aArr.length > 1) {
      aArr.forEach(a => {
        if (a.includes(urlName)) {
          chosenAtag.push(a);
        }
      });
    } else {
      chosenAtag = aArr;
    }

    // Prepare in-app-link and select one of three options:
    let inAppLink;

    // 1. open internal in-app-link
    if (chosenAtag[0].includes('data-in-app-link-v3')) {
      inAppLink = chosenAtag[0].match(
        /<a\s+(?:[^>]*?\s+)?data-in-app-link-v3=(["'])(.*?)\1/
      );
      if (inAppLink && inAppLink[2]) {
        this.openInAppLink(inAppLink);
      }
    }

    // 2. open external link
    if (
      (chosenAtag[0].includes('href') && chosenAtag[0].includes('://')) ||
      chosenAtag[0].includes('mailto') ||
      chosenAtag[0].includes('tel')
    ) {
      const href = chosenAtag[0].match(/<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/);

      if (href && !inAppLink) {
        this.openExternalLink(href);
      }
    }

    // 3. open internal short data-router-link
    if (chosenAtag[0].includes('data-router-link')) {
      const routerLink = chosenAtag[0].match(
        /<a\s+(?:[^>]*?\s+)?data-router-link=(["'])(.*?)\1/
      );
      if (routerLink) {
        this.openDataRouterLink(routerLink);
      }
    }
  }

  openInAppLink(inAppLink) {
    const bHref = inAppLink[0].match(
      /<a\s+(?:[^>]*?\s+)?data-in-app-link-v3=(["'])(.*?)\1/
    );

    const subType = inAppLink[0].match(
      /<a\s+(?:[^>]*?\s+)?data-in-app-sub-type=(["'])(.*?)\1/
    );

    const convertResource = (type: string, href: string) => {
      switch (type) {
        case 'form':
          return { type: CoreMobiResourceType.FORM, href };
        case 'category':
          return { type: CoreMobiResourceType.CATEGORY, href };
        case 'forms-container':
          return { type: CoreMobiResourceType.CATEGORY, href };
        case 'fiche':
          return { type: CoreMobiResourceType.FACTSHEET, href };
        case 'quizz':
          return { type: CoreMobiResourceType.QUIZZ, href };
        case 'chatbot':
          return { type: CoreMobiResourceType.CHATBOT, href };
        case 'body-schema-pain':
          return { type: CoreMobiResourceType.BODY_SCHEMA, href };
        default:
          return void 0;
      }
    };

    const resource = convertResource(subType[2], bHref[2]);

    const button = {
      label: '',
      resource: resource
    };

    // const improvedButton: ImprovedCoreMenuButton = button;

    this.store.dispatch(
      new CoreActionNavigateToResource({
        resource: button.resource
      })
    );
  }

  openDataRouterLink(dataRouterLink) {
    const convertButton = (module: string) => {
      switch (module) {
        case 'timeline':
          return { module: CorePageModuleIDs.TIMELINE };
        case 'mail':
          return { module: CorePageModuleIDs.MESSAGES };
        case 'dashboard':
          return { module: CorePageModuleIDs.MY_MEASURES };
        case 'measures':
          return { module: CorePageModuleIDs.TAKE_MEASURE };
        case 'patient-events':
          return { module: CorePageModuleIDs.EVENTS };
        case 'profile':
          return { module: CorePageModuleIDs.MY_ACCOUNT };
        default:
          return void 0;
      }
    };
    // Set route for the improvedButton
    const improvedButton = convertButton(dataRouterLink[2]);

    this.store.dispatch(
      new CoreRouterActionNavigateToPageModule({
        id: improvedButton.module
      })
    );
  }

  openExternalLink(href) {
    if (href && href[2]) {
      // If the href attribute does not include '://' it must not go to external link
      if (
        !href[2].includes('://') &&
        !href[2].includes('mailto') &&
        !href[2].includes('tel')
      ) {
        // console.log('ERROR: Redundunt HTML attribute');
      } else {
        // Kept for security
        // this.utils.openExternalUrlInBrowser(href[2]);
        this.coreUtils.navigateToExternalUrl(href[2]);
      }
    } else {
      // If href is null or empty
      // console.log('ERROR');
    }
  }
}
